import Phaser from 'phaser';

import { Subscription } from 'rxjs';
import { CardViewModel } from './card-view-model';
import { CardFrontView } from './card-front-view';
import { CardBackView } from './card-back-view';
import { CardShadowView } from './card-shadow-view';
import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { ResizeMetrics } from '../../model/resize-metrics';
import { UxMetrics } from '../../model/ux-metrics';

export class CardView extends Phaser.GameObjects.Container {
  public get cardIndex() {
    return this.viewModel.cardIndex;
  }

  private subscriptionGroup: Subscription;
  private cardShadowView: CardShadowView;
  private cardBackView: CardBackView;
  private cardFrontView: CardFrontView;
  private flipTween?: Phaser.Tweens.Tween;

  private flipValue = 0;
  private isShowingFace = false;

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: CardViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.cardShadowView = new CardShadowView(scene, 0, 0, viewModel);
    this.cardBackView = new CardBackView(scene, 0, 0, viewModel);
    this.cardFrontView = new CardFrontView(scene, 0, 0, viewModel);
    this.cardFrontView.setVisible(false);
    this.add([this.cardShadowView, this.cardBackView, this.cardFrontView]);

    this.setName(CardView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.currentDepth$.subscribe((value) => {
        this.depth = value;
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.showShadow$.subscribe((show) => {
        this.cardShadowView.setVisible(show);
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    if (this.flipTween) {
      this.flipTween.stop();
      this.scene.tweens.remove(this.flipTween);
    }

    this.subscriptionGroup.unsubscribe();
    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics): void {
    this.cardShadowView.onResize(resizeMetrics);
    this.cardBackView.onResize(resizeMetrics);
    this.cardFrontView.onResize(resizeMetrics);

    const cardScaleFactor = resizeMetrics.scalingMetrics.scaleFactor * 0.8;

    const gameAsset = this.viewModel.cardBackAsset;
    const displayWidth = gameAsset.dimensions.width * cardScaleFactor;
    const displayHeight = gameAsset.dimensions.height * cardScaleFactor;

    this.setSize(displayWidth, displayHeight);
  }

  public flipCard(isFaceUp: boolean, animated: boolean) {
    if (this.flipTween !== undefined && this.flipTween.isPlaying()) {
      this.flipTween.stop();
      this.flipValue = 0;
      this.scaleX = 1;
    }

    if (!animated) {
      if (this.isShowingFace !== isFaceUp) {
        this.cardBackView.visible = !isFaceUp;
        this.cardFrontView.visible = isFaceUp;
        this.isShowingFace = isFaceUp;
      }
      this.flipValue = 0;
      this.scaleX = 1;
      return;
    }

    this.flipTween = this.scene.tweens.add({
      targets: this,
      flipValue: 1,
      duration: UxMetrics.CardFlipDelay,
      yoyo: true,
      ease: Phaser.Math.Easing.Quadratic.In,
      onYoyo: (_tween, _target, isYoyo) => {
        if (isYoyo) {
          if (this.isShowingFace !== isFaceUp) {
            this.cardBackView.visible = !isFaceUp;
            this.cardFrontView.visible = isFaceUp;
            this.isShowingFace = isFaceUp;
          }
        }
      },
      onUpdate: (_tween) => {
        this.scaleX = 1 - this.flipValue;
      },
      onComplete: () => {
        this.flipValue = 0;
        this.scaleX = 1;
      },
    });
  }
}
